import { FC } from 'react';
import { Card, CardContent, CardHeader, Typography } from '@mui/material';
import { Device, DeviceDetailedHealth } from 'types';
import DataLabelPairing from 'components/DataLabelPairing/DataLabelPairing';
import DeviceBatteryIcon from 'components/Devices/DeviceBatteryIcon/DeviceBatteryIcon';
import DeviceSignalIcon from 'components/Devices/DeviceSignalIcon/DeviceSignalIcon';
import {
    GenericStatusIcon,
    statusClassState,
} from 'components/Devices/GenericStatusIcon/GenericStatusIcon';
import styles from './DeviceHealthMainCard.module.scss';

interface DeviceHealthMainCardProps {
    device: Device;
    healthData: DeviceDetailedHealth | null;
    healthFlags: string[];
    healthRatingClass: statusClassState;
    rsrpClass: statusClassState;
    rsrqClass: statusClassState;
    successRateClass: statusClassState;
    offPropClass: statusClassState;
    rsrp: number | null;
    rsrq: number | null;
}

const DeviceHealthMainCard: FC<DeviceHealthMainCardProps> = ({
    device,
    healthData,
    healthFlags,
    healthRatingClass,
    rsrpClass,
    rsrqClass,
    successRateClass,
    offPropClass,
    rsrp,
    rsrq,
}) => (
    <div
        className={styles.DeviceHealthMainCard}
        data-testid="DeviceHealthMainCard"
    >
        <Card>
            <CardHeader
                title={
                    <div className={styles.titleRow}>
                        <Typography>{device.name}</Typography>
                        <Typography
                            data-testid="ProductId"
                            className={styles.monospaced}
                        >
                            {device.product_id.toUpperCase()}
                        </Typography>
                        <span>
                            <DeviceSignalIcon {...device}></DeviceSignalIcon>
                        </span>

                        <span>
                            <DeviceBatteryIcon
                                battery={device.lastReading}
                                itemNumber={device.item_number}
                            />
                        </span>
                    </div>
                }
                subheader={
                    <div>
                        <div>{device.name}</div>
                        <div>
                            {!!device.address
                                ? device.address
                                : 'No address entered'}
                        </div>
                        <div>{device.shortDeviceId}</div>
                        {!!healthFlags?.length && (
                            <div>
                                <div className={styles.healthFlagHeader}>
                                    Flags:
                                </div>
                                {healthFlags.map((flag) => (
                                    <div
                                        className={styles.healthFlag}
                                        key={healthFlags.indexOf(flag)}
                                    >
                                        {flag}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                }
            />
            <CardContent>
                <DataLabelPairing
                    label="Health Rating"
                    data={
                        <div className={styles.dataIconPairing}>
                            <div>{healthData?.healthy || '?'}</div>
                            <GenericStatusIcon status={healthRatingClass} />
                        </div>
                    }
                />
                <DataLabelPairing
                    label="Signal Power (Avg.)"
                    data={
                        <div className={styles.dataIconPairing}>
                            <div>{rsrp?.toFixed(0) || '?'}dBm</div>
                            <GenericStatusIcon status={rsrpClass} />
                        </div>
                    }
                />
                <DataLabelPairing
                    label="Signal Quality (Avg.)"
                    data={
                        <div className={styles.dataIconPairing}>
                            <div>{rsrq?.toFixed(0) || '?'}db</div>
                            <GenericStatusIcon status={rsrqClass} />
                        </div>
                    }
                />
                <DataLabelPairing
                    label="Success Rate"
                    data={
                        <div className={styles.dataIconPairing}>
                            <div>
                                {healthData?.rate
                                    ? (healthData.rate * 100).toFixed(2)
                                    : '?'}
                                %
                            </div>
                            <GenericStatusIcon status={successRateClass} />
                        </div>
                    }
                />
                <DataLabelPairing
                    label="Offline Rate"
                    data={
                        <div className={styles.dataIconPairing}>
                            <div>
                                {healthData?.off_prop !== undefined
                                    ? healthData.off_prop.toFixed(2)
                                    : '?'}
                                %
                            </div>
                            <GenericStatusIcon status={offPropClass} />
                        </div>
                    }
                />
            </CardContent>
        </Card>{' '}
    </div>
);

export default DeviceHealthMainCard;
